// Card 05, the graticule (R548, ui-map-interface.md §17.3.05).
//
// A chart carries its latitude and longitude along the four edges, and so
// does this one: the chart frame of `ui/chartpos.ts` (R2742), where one
// cell is one minute of arc. The longitude of a column stands along the top
// and the bottom, the latitude of a row along the two sides, one figure
// every `every_cells` cells. Every peek states its position in the same
// figures, so a reader finds the place of a card on the edge, and a reader
// can name a place in chat that another reader finds on their own screen.
// Where the figures would touch at a far zoom, the piece writes one figure
// in two lines, or in three, and keeps every tick.
//
// **It writes on the four edges of the screen** (R1812, R1867, R2373),
// inside the safe inset and with no pad, and never under a piece of the
// chrome (the dock is a box it skips). A figure or a tick that touches a
// slot of the corner grid (`ui/cornergrid.ts`) is skipped. The top row also
// skips the boxes of the chrome on the top edge, so its figures stand left
// and right of the readout line. A
// page with no chrome hands no grid, and the edges are the canvas's own.
// The figures take the label step of the piece's `text` table (R1796).
//
// Under `grid_zoom_max` a faint grid joins the two edges, so the figures
// have lines to belong to when the whole world is on the glass.
//
// **It is LIVE.** Every figure moves with the pan, so there is nothing to
// pack once: a static pack would have to be thrown away on every frame the
// camera moved, which is most of them.
//
// **It yields to the border band.** Card 39 draws the same four edges as a
// frame of divisions. While that piece is on (`ctx.bandOn`) the graticule
// draws no tick and no chequer — the band's own divisions are the ticks —
// and its figures move inside the band by `figure_inset`.
//
// **One tier.** The contract gives a piece ONE layer, so the faint grid
// rides the screen tier with the figures rather than the sea tier. At
// `grid_alpha` it lies under nothing it could hide.

import { F_SCREEN } from '../../layer';
import type { FrameInfo } from '../../layer';
import { COL_PITCH, HEX_SIZE } from '../../../gen/balance';
import type { ChartContext, ChartSinks, Piece } from '../context';
import type { ChartFace } from '../../gfx/atlas';
import type { RunOpts } from '../glyphs';
import { stepPx } from '../steps';
import { clearOfSlots, type CornerGrid, type GridRect } from '../../../ui/cornergrid';
import { CHART_INK, ROW_PITCH, colLine, faceOf, rowY } from './shared';
import { colLon, latFig, lonFig, rowLat } from '../../../ui/chartpos';

/** The share of the size a figure of the mono and the Fell face is wide,
 *  per character: a bound for the skip test, not a layout. */
const CHAR_W = 0.62;

/** The box of the skip test, rewritten in place so no frame allocates one. */
const BOX: GridRect = { x: 0, y: 0, w: 0, h: 0 };

/**
 * Is a box of the screen clear of the chrome? Every figure and every tick
 * skips the slots of the corner grid, and one of the top row also skips
 * the boxes on the top edge (R1867). No grid is no chrome.
 */
function clearOfChrome(
  grid: CornerGrid | null, x: number, y: number, w: number, h: number,
  margin: number, top: boolean,
): boolean {
  if (grid === null) return true;
  BOX.x = x; BOX.y = y; BOX.w = w; BOX.h = h;
  return clearOfSlots(BOX, grid.skip, margin) && (!top || clearOfSlots(BOX, grid.topSkip, margin));
}

export class Graticule implements Piece {
  readonly id = 'graticule' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = ['reading'] as const;

  // The frame in hand, which `put`, `tick`, `wx` and `wy` read; `emit`
  // writes it, so a frame makes no closure.
  private sinks!: ChartSinks;
  private ctx!: ChartContext;
  private grid: CornerGrid | null = null;
  private x0 = 0;
  private y0 = 0;
  private z = 1;
  private face: ChartFace = 'mono';
  private size = 0;
  private figAlpha = 0;
  private margin = 0;
  private tickWidth = 0;
  private tickAlpha = 0;
  /** The options of a figure run, rewritten per figure. */
  private readonly opts: RunOpts = { anchor: 'centre', screen: true, zoom: 1 };

  /** Nothing is built ahead: every figure is a function of the camera. */
  rebuild(_ctx: ChartContext): void { /* the camera is the whole state */ }

  /** A screen x or y as a world x or y. */
  private wx(sx: number): number { return this.x0 + sx / this.z; }
  private wy(sy: number): number { return this.y0 + sy / this.z; }

  /** One figure at a point of the screen, unless it would touch the chrome. */
  private put(text: string, sx: number, sy: number, anchor: 'left' | 'centre' | 'right', top = false): void {
    const size = this.size;
    const bw = text.length * size * CHAR_W;
    const bx = anchor === 'centre' ? sx - bw / 2 : anchor === 'right' ? sx - bw : sx;
    if (!clearOfChrome(this.grid, bx, sy - size / 2, bw, size, this.margin, top)) return;
    const o = this.opts;
    o.anchor = anchor; o.zoom = this.z;
    this.ctx.glyphs.run(
      this.sinks.quads, text, this.wx(sx), this.wy(sy), this.face, size, CHART_INK.pale, this.figAlpha, o,
    );
  }

  /** One tick between two points of the screen, unless it would touch the chrome. */
  private tick(x0: number, y0: number, x1: number, y1: number, top = false): void {
    const bx = Math.min(x0, x1), by = Math.min(y0, y1);
    if (!clearOfChrome(this.grid, bx, by, Math.abs(x1 - x0), Math.abs(y1 - y0), this.margin, top)) return;
    this.sinks.segments.line(
      this.wx(x0), this.wy(y0), this.wx(x1), this.wy(y1), this.tickWidth, CHART_INK.pale, this.tickAlpha, F_SCREEN,
    );
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.graticule;
    const f = t.fig;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const z = ctx.zoom;
    const w = ctx.cam.w, h = ctx.cam.h;
    const face = faceOf(ctx.reading);
    const size = stepPx(face, t.text.main);
    // The edges the figures stand on: the screen inside the safe inset with
    // no pad (R1867, R2373), or the canvas while the border band frames it
    // or no grid is handed. The row figures start under the readout line.
    const band = ctx.bandOn;
    const grid = band ? null : ctx.edges;
    const L = grid ? grid.screen.x : 0;
    const T = grid ? grid.clear.y : 0;
    const R = grid ? grid.screen.x + grid.screen.w : w;
    const B = grid ? grid.screen.y + grid.screen.h : h;
    // The top row skips the chrome on the top edge as well.
    const TOP = grid ? grid.screen.y : 0;
    this.sinks = sinks; this.ctx = ctx; this.grid = grid;
    this.x0 = ctx.view.x0; this.y0 = ctx.view.y0; this.z = z;
    this.face = face; this.size = size; this.figAlpha = f.figure_alpha;
    this.margin = f.slot_margin; this.tickWidth = r.width; this.tickAlpha = f.tick_alpha;

    // The faint grid, in world px. It joins the two edges under the grid
    // zoom and goes when the reader is close enough to count cells.
    const step = f.every_cells;
    const c0 = Math.floor(ctx.view.x0 / (COL_PITCH * step)) * step;
    const c1 = Math.ceil(ctx.view.x1 / (COL_PITCH * step)) * step;
    const r0 = Math.floor((ctx.view.y0 - HEX_SIZE) / (ROW_PITCH * step)) * step;
    const r1 = Math.ceil((ctx.view.y1 - HEX_SIZE) / (ROW_PITCH * step)) * step;
    if (z <= f.grid_zoom_max) {
      for (let c = c0; c <= c1; c += step) {
        const x = colLine(c);
        sinks.segments.line(
          x, ctx.view.y0, x, ctx.view.y1,
          f.grid_width, CHART_INK.pale, f.grid_alpha, F_SCREEN,
        );
      }
      for (let row = r0; row <= r1; row += step) {
        const y = rowY(row);
        sinks.segments.line(
          ctx.view.x0, y, ctx.view.x1, y,
          f.grid_width, CHART_INK.pale, f.grid_alpha, F_SCREEN,
        );
      }
    }

    // The four edges. `bandOn` takes the ticks and the chequer away and
    // moves the figures inside the band.
    const inset = f.figure_inset;
    const colPx = COL_PITCH * step * z;
    const rowPx = ROW_PITCH * step * z;
    // The middle of a column figure, and the x of a row figure, off the
    // edge: past the tick and its air, or inside the band.
    const across = band ? inset : f.tick_px + f.figure_gap + size / 2;
    const beside = band ? inset : f.tick_px + f.figure_gap;
    // How many lines one figure stands for: a figure needs its own width
    // and one em of air along the top, and its height and half an em along
    // a side (R2742).
    const colEvery = strideOf(lonFig(colLon(0)).length * size * CHAR_W + size, colPx);
    const rowEvery = strideOf(size * 1.5, rowPx);

    for (let c = c0, i = 0; c <= c1; c += step, i++) {
      const sx = (colLine(c) - ctx.view.x0) * z;
      if (sx < L || sx > R) continue;
      if (!band) {
        if (hist && i % 2 === 0) {
          const top = this.wy(TOP + f.chequer_px / 2), bottom = this.wy(B - f.chequer_px / 2);
          chequer(sinks, this.wx(sx), top, this.wx(sx + colPx), top, f);
          chequer(sinks, this.wx(sx), bottom, this.wx(sx + colPx), bottom, f);
        }
        this.tick(sx, TOP, sx, TOP + f.tick_px, true);
        this.tick(sx, B, sx, B - f.tick_px);
      }
      // A chart's figure straddles the graduation it names (R805): the
      // column figure is centred on its tick, never set after it, so it
      // agrees with the neat line (card 62) and the border band (card 39).
      // One too near a corner of the clear map is dropped.
      if (sx < L + f.figure_margin || sx > R - f.figure_margin) continue;
      if (!onStride(c / step, colEvery)) continue;
      const lon = lonFig(colLon(c));
      this.put(lon, sx, TOP + across, 'centre', true);
      this.put(lon, sx, B - across, 'centre');
    }

    for (let row = r0, i = 0; row <= r1; row += step, i++) {
      const sy = (rowY(row) - ctx.view.y0) * z;
      if (sy < T || sy > B) continue;
      if (!band) {
        if (hist && i % 2 === 1) {
          const left = this.wx(L + f.chequer_px / 2), right = this.wx(R - f.chequer_px / 2);
          chequer(sinks, left, this.wy(sy), left, this.wy(sy + rowPx), f);
          chequer(sinks, right, this.wy(sy), right, this.wy(sy + rowPx), f);
        }
        this.tick(L, sy, L + f.tick_px, sy);
        this.tick(R, sy, R - f.tick_px, sy);
      }
      if (!onStride(row / step, rowEvery)) continue;
      const lat = latFig(rowLat(row));
      this.put(lat, L + beside, sy, 'left');
      this.put(lat, R - beside, sy, 'right');
    }
  }
}

/** How many lines one figure takes, so that `need` px fit in `every × gap`. */
function strideOf(need: number, gap: number): number {
  return gap > 0 ? Math.max(1, Math.ceil(need / gap)) : 1;
}

/** Does line `k` (counted in lines from the origin) carry a figure? */
function onStride(k: number, every: number): boolean {
  const n = Math.round(k);
  return ((n % every) + every) % every === 0;
}

/** One chequer of the historical band along an edge, in world px. */
function chequer(
  s: ChartSinks, x0: number, y0: number, x1: number, y1: number,
  f: { chequer_px: number; chequer_alpha: number },
): void {
  s.segments.line(x0, y0, x1, y1, f.chequer_px, CHART_INK.pale, f.chequer_alpha, F_SCREEN);
}

/** The one instance the registry holds. */
export const graticule = new Graticule();
